import type { Lang } from '../../i18n/types';
import type { CheckinGuideConfig } from '../../api.checkinGuide';

/**
 * Ready-made starting text for the Guide fields a new host usually leaves empty (#131).
 *
 * Samples are data, not layout: the editor offers them per field, the save path and the manager
 * preview call `withoutUneditedSamples`, and nothing here knows where a field sits on the page.
 *
 * Rules every entry follows (the tests enforce the checkable ones):
 *  - one short bullet per line, no prose, same style in every language;
 *  - no digits, no '@', no URL: a value only the host knows is a fill-in marker, never a
 *    realistic fake (a believable Wi-Fi password or gate code is worse than none).
 *
 * Wi-Fi and host contact are structured inputs (name, password, phone), where a sample could only
 * be a fake value. Their sample lives as fill-in bullets in the detailed guide instead.
 */
export type GuideSampleField = 'parkingInfo' | 'detailedGuide' | 'attentionNotes' | 'checkoutInstructions';

export const GUIDE_SAMPLE_FIELDS: readonly GuideSampleField[] = [
  'parkingInfo', 'detailedGuide', 'attentionNotes', 'checkoutInstructions',
];

const bullets = (...lines: string[]) => lines.map(l => `• ${l}`).join('\n');

export const GUIDE_SAMPLES: Record<Lang, Record<GuideSampleField, string>> = {
  cs: {
    parkingInfo: bullets(
      'Parkování: doplňte místo (např. před domem, garáž, placené stání)',
      'Cena parkování: doplňte, nebo napište „zdarma“',
      'Vjezd / brána: doplňte, jak se dostat dovnitř',
    ),
    detailedGuide: bullets(
      'Wi-Fi: název a heslo doplňte v sekci Wi-Fi výše',
      'Topení a klimatizace: doplňte, kde se ovládá',
      'Spotřebiče (trouba, pračka, myčka): doplňte, kde najdou návod',
      'Odpadky a tříděný odpad: doplňte, kam s nimi',
      'Pravidla domu a noční klid: doplňte od kolika hodin',
      'Kontakt na hostitele: doplňte v sekci Kontakt výše',
      'V nouzi volejte 112',
    ),
    attentionNotes: bullets(
      'Doplňte, co host nesmí přehlédnout (např. zákaz kouření)',
      'Doplňte pravidla pro hlasitou hudbu a večírky',
      'Doplňte případný poplatek za ztrátu klíče',
    ),
    checkoutInstructions: bullets(
      'Zavřete okna a zhasněte světla',
      'Vypněte topení nebo klimatizaci',
      'Vyneste odpadky',
      'Klíče: doplňte, kam je vrátit',
      'Zamkněte za sebou dveře',
    ),
  },
  en: {
    parkingInfo: bullets(
      'Parking: fill in where (e.g. in front of the building, garage, paid spot)',
      'Parking price: fill in, or write "free"',
      'Gate / entrance: fill in how to get in',
    ),
    detailedGuide: bullets(
      'Wi-Fi: fill in the name and password in the Wi-Fi section above',
      'Heating and air conditioning: fill in where to control them',
      'Appliances (oven, washer, dishwasher): fill in where the manuals are',
      'Rubbish and recycling: fill in where to put it',
      'House rules and quiet hours: fill in from what time',
      'Host contact: fill in the Contact section above',
      'In an emergency call 112',
    ),
    attentionNotes: bullets(
      'Fill in what guests must not miss (e.g. no smoking)',
      'Fill in your rules on loud music and parties',
      'Fill in any fee for a lost key',
    ),
    checkoutInstructions: bullets(
      'Close the windows and switch off the lights',
      'Turn off the heating or air conditioning',
      'Take out the rubbish',
      'Keys: fill in where to return them',
      'Lock the door behind you',
    ),
  },
  vi: {
    parkingInfo: bullets(
      'Chỗ đỗ xe: điền vị trí (ví dụ trước nhà, hầm gửi xe, chỗ trả phí)',
      'Giá đỗ xe: điền vào, hoặc ghi "miễn phí"',
      'Cổng / lối vào: điền cách vào',
    ),
    detailedGuide: bullets(
      'Wi-Fi: điền tên và mật khẩu ở mục Wi-Fi phía trên',
      'Sưởi và điều hòa: điền nơi điều khiển',
      'Thiết bị (lò nướng, máy giặt, máy rửa bát): điền nơi để sách hướng dẫn',
      'Rác và phân loại rác: điền nơi bỏ rác',
      'Nội quy và giờ yên tĩnh: điền từ mấy giờ',
      'Liên hệ chủ nhà: điền ở mục Liên hệ phía trên',
      'Khẩn cấp hãy gọi 112',
    ),
    attentionNotes: bullets(
      'Điền những điều khách không được bỏ qua (ví dụ cấm hút thuốc)',
      'Điền quy định về nhạc lớn và tiệc tùng',
      'Điền phí nếu làm mất chìa khóa',
    ),
    checkoutInstructions: bullets(
      'Đóng cửa sổ và tắt đèn',
      'Tắt sưởi hoặc điều hòa',
      'Đổ rác',
      'Chìa khóa: điền nơi trả lại',
      'Khóa cửa khi ra về',
    ),
  },
  uk: {
    parkingInfo: bullets(
      'Паркування: заповніть де (наприклад перед будинком, гараж, платне місце)',
      'Ціна паркування: заповніть або напишіть «безкоштовно»',
      'Ворота / вхід: заповніть як потрапити всередину',
    ),
    detailedGuide: bullets(
      'Wi-Fi: назву й пароль заповніть у розділі Wi-Fi вище',
      'Опалення та кондиціонер: заповніть де ними керувати',
      'Техніка (духовка, пральна й посудомийна машини): заповніть де інструкції',
      'Сміття та сортування: заповніть куди його нести',
      'Правила дому й тиша: заповніть з котрої години',
      'Контакт господаря: заповніть у розділі Контакт вище',
      'У надзвичайній ситуації телефонуйте 112',
    ),
    attentionNotes: bullets(
      'Заповніть що гість не повинен пропустити (наприклад заборона куріння)',
      'Заповніть правила щодо гучної музики та вечірок',
      'Заповніть можливу плату за загублений ключ',
    ),
    checkoutInstructions: bullets(
      'Зачиніть вікна й вимкніть світло',
      'Вимкніть опалення або кондиціонер',
      'Винесіть сміття',
      'Ключі: заповніть куди їх повернути',
      'Зачиніть двері на ключ',
    ),
  },
};

const normalise = (text: string) => text.replace(/\r\n/g, '\n').trim();

export const sampleFor = (lang: Lang, field: GuideSampleField): string => GUIDE_SAMPLES[lang][field];

const allSamplesOf = (field: GuideSampleField): string[] =>
  (Object.keys(GUIDE_SAMPLES) as Lang[]).map(l => normalise(GUIDE_SAMPLES[l][field]));

/**
 * True when `text` is still exactly one of the shipped samples. Compared against every language,
 * not just the editor's current one: a host who switched the interface language after inserting
 * must not have the old sample slip through to guests as their own instructions.
 */
export const isUneditedSample = (field: GuideSampleField, text: string | null | undefined): boolean =>
  !!text && allSamplesOf(field).includes(normalise(text));

/**
 * The Guide as guests may see it: any field still holding an untouched sample becomes empty.
 * The sample is a starting point, not the host's own instruction, and the public Guide has no way
 * to tell the two apart, so the draft is cleaned here before it is saved or previewed.
 * The `*En` twin is cleaned too (an English sample can sit in either box).
 */
export const withoutUneditedSamples = (config: CheckinGuideConfig): CheckinGuideConfig => {
  let next = config;
  for (const field of GUIDE_SAMPLE_FIELDS) {
    const enKey = `${field}En` as const;
    if (isUneditedSample(field, next[field])) next = { ...next, [field]: '' };
    if (isUneditedSample(field, next[enKey])) next = { ...next, [enKey]: '' };
  }
  return next;
};
